/* Schema-driven CMS editor.
 * - For singleton schemas (site_settings, navigation, founder, form_configs):
 *   loads the single document and presents a single form.
 * - For list schemas (books, founding_members, etc.):
 *   shows a list on the left, an edit pane on the right.
 *
 * Always presents form-based editing by default.
 * An "Advanced (developer JSON)" toggle is available for power users.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi, formatApiError } from "@/lib/api";
import { ChevronLeft, Plus, Trash2, Save, X, Code2, FileText } from "lucide-react";
import { SCHEMAS } from "./_schemas";
import SchemaForm, { validate } from "./_SchemaForm";
import { useDataLoader } from "./_useDataLoader";

function SavedFlash({ ts }) {
  const [show, setShow] = useState(true);
  useDataLoader(async () => {
    setShow(true);
    const t = setTimeout(() => setShow(false), 3500);
    return () => clearTimeout(t);
  }, [ts]);
  if (!show) return null;
  return <span className="text-green-700">· Saved</span>;
}

export default function AdminSchemaEditor() {
  const { name } = useParams();
  const schema = SCHEMAS[name];

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // null | "new" | item.id
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [advanced, setAdvanced] = useState(false);
  const [jsonDraft, setJsonDraft] = useState("");
  const [jsonErr, setJsonErr] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const isSingleton = schema?.singleton;

  const reload = async () => {
    if (!schema) return;
    try {
      const data = await adminApi.list(schema.collection);
      setItems(data);
      if (isSingleton) {
        // Auto-select the singleton (create if missing)
        let doc = data && data[0];
        if (!doc) {
          const key = schema.singletonKey;
          const empty = key ? { [key.field]: key.value } : {};
          doc = await adminApi.create(schema.collection, empty);
          setItems([doc]);
        }
        setSelectedId(doc.id);
        setDraft(doc);
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load");
    }
  };

  useDataLoader(async () => {
    setSelectedId(null);
    setDraft({});
    setErrors({});
    setAdvanced(false);
    setJsonErr("");
    setErr("");
    if (schema) {
      document.title = `BSCA Admin · ${schema.title}`;
      await reload();
    }
  }, [name]);

  const labelFn = useMemo(
    () => schema?.labelField || ((d) => d.title || d.name || d.slug || d.id),
    [schema]
  );
  const sublabelFn = useMemo(
    () => schema?.sublabelField || ((d) => d.status || ""),
    [schema]
  );

  if (!schema) {
    return (
      <div className="p-12">
        <Link to="/admin/dashboard" className="bsca-link text-xs"><ChevronLeft className="h-3 w-3 inline mr-1" /> Dashboard</Link>
        <h1 className="mt-3 font-serif text-3xl">Unknown module: {name}</h1>
        <p className="text-[#4B5563] text-sm mt-1">This collection has no schema yet.</p>
      </div>
    );
  }

  const startEdit = (item) => {
    setSelectedId(item.id);
    const copy = { ...item };
    delete copy.id;
    setDraft(copy);
    setErrors({});
    setErr("");
    setAdvanced(false);
    setJsonDraft(JSON.stringify(copy, null, 2));
    setJsonErr("");
  };

  const startNew = () => {
    setSelectedId("new");
    const initial = { ...(schema.newDefaults || {}) };
    setDraft(initial);
    setErrors({});
    setErr("");
    setAdvanced(false);
    setJsonDraft(JSON.stringify(initial, null, 2));
    setJsonErr("");
  };

  const cancel = () => {
    if (isSingleton) return; // singletons stay open
    setSelectedId(null);
    setDraft({});
    setErrors({});
    setErr("");
  };

  const save = async () => {
    setErr("");
    let payload = draft;
    if (advanced) {
      try {
        payload = JSON.parse(jsonDraft);
      } catch (e) {
        setJsonErr("Invalid JSON: " + e.message);
        return;
      }
      setJsonErr("");
    } else {
      const errs = validate(schema, draft);
      setErrors(errs);
      if (Object.keys(errs).length > 0) return;
    }
    setBusy(true);
    try {
      if (selectedId === "new") {
        const created = await adminApi.create(schema.collection, payload);
        await reload();
        if (!isSingleton) startEdit(created);
      } else {
        await adminApi.update(schema.collection, selectedId, payload);
        const fresh = await adminApi.list(schema.collection);
        setItems(fresh);
        const current = fresh.find((i) => i.id === selectedId);
        if (current) setDraft({ ...current, id: undefined });
      }
      setSavedAt(Date.now());
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${labelFn(item)}"? This cannot be undone.`)) return;
    try {
      await adminApi.remove(schema.collection, item.id);
      setSelectedId(null);
      setDraft({});
      await reload();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Delete failed.");
    }
  };

  /* Sync JSON when switching to advanced */
  const toggleAdvanced = (val) => {
    if (val) setJsonDraft(JSON.stringify(draft, null, 2));
    else {
      try {
        const parsed = JSON.parse(jsonDraft);
        setDraft(parsed);
        setJsonErr("");
      } catch (e) {
        setJsonErr("Invalid JSON: " + e.message);
        return;
      }
    }
    setAdvanced(val);
  };

  return (
    <div className="p-8 md:p-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <Link to="/admin/dashboard" className="bsca-link text-xs text-[#4B5563] inline-flex items-center">
            <ChevronLeft className="h-3 w-3 mr-1" /> Dashboard
          </Link>
          <h1 className="mt-3 font-serif text-4xl tracking-tight">{schema.title}</h1>
          {!isSingleton && (
            <p className="text-[#4B5563] text-sm mt-1">{items.length} {items.length === 1 ? "entry" : "entries"}</p>
          )}
        </div>
        {!isSingleton && (
          <button
            onClick={startNew}
            data-testid="btn-new-entry"
            className="bsca-lift bg-[#D4AF37] text-[#050B14] font-medium px-5 py-3 rounded-sm inline-flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" /> New entry
          </button>
        )}
      </div>

      <div className={`grid grid-cols-1 ${isSingleton ? "" : "lg:grid-cols-12"} gap-6`}>
        {!isSingleton && (
          <div className="lg:col-span-4 bg-white border border-black/10">
            <ul className="divide-y divide-black/10 max-h-[70vh] overflow-y-auto">
              {items.length === 0 && <li className="p-6 text-[#9CA3AF] text-sm">No entries yet.</li>}
              {items.map((it) => (
                <li key={it.id} className={`flex items-center justify-between ${selectedId === it.id ? "bg-[#FBF3D5]/40" : "hover:bg-[#F8F6F1]"}`}>
                  <button
                    data-testid={`admin-item-${it.id}`}
                    className="flex-1 text-left px-5 py-4 min-w-0"
                    onClick={() => startEdit(it)}
                  >
                    <div className="font-serif text-base truncate">{labelFn(it)}</div>
                    <div className="text-xs text-[#9CA3AF] truncate">{sublabelFn(it)}</div>
                  </button>
                  <button
                    onClick={() => remove(it)}
                    data-testid={`btn-delete-${it.id}`}
                    className="p-3 text-[#9CA3AF] hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={isSingleton ? "" : "lg:col-span-8"}>
          {selectedId === null ? (
            <div className="bg-white border border-black/10 p-12 text-center text-[#9CA3AF]">
              Select an entry on the left, or create a new one.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="bsca-eyebrow text-[#7A5A06] text-[10px] inline-flex items-center gap-2">
                  {advanced ? <Code2 className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                  {selectedId === "new" ? "New entry" : `Editing ${labelFn(draft) || ""}`}
                  {savedAt > 0 && <SavedFlash ts={savedAt} />}
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs text-[#4B5563]">
                    <input
                      type="checkbox"
                      checked={advanced}
                      onChange={(e) => toggleAdvanced(e.target.checked)}
                      data-testid="toggle-advanced"
                    />
                    Advanced (developer JSON)
                  </label>
                </div>
              </div>

              {err && (
                <div className="text-red-700 text-sm border border-red-300 bg-red-50 px-4 py-2 rounded-sm" data-testid="schema-editor-error">
                  {err}
                </div>
              )}

              {advanced ? (
                <div>
                  <textarea
                    data-testid="advanced-json-editor"
                    value={jsonDraft}
                    onChange={(e) => setJsonDraft(e.target.value)}
                    spellCheck={false}
                    rows={22}
                    className="w-full font-mono text-xs leading-relaxed bg-[#0A1120] text-[#F8F6F1] border border-black/10 p-4 rounded-sm focus:outline-none focus:border-[#D4AF37]"
                  />
                  {jsonErr && <div className="mt-2 text-red-700 text-sm" data-testid="advanced-json-error">{jsonErr}</div>}
                  <p className="mt-2 text-xs text-[#9CA3AF]">Advanced view for developers. The form view is recommended for safe editing.</p>
                </div>
              ) : (
                <SchemaForm schema={schema} value={draft} onChange={setDraft} errors={errors} />
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={save}
                  disabled={busy}
                  data-testid="btn-save-entry"
                  className="bsca-lift inline-flex items-center bg-[#050B14] text-white px-6 py-3 rounded-sm disabled:opacity-60"
                >
                  <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save"}
                </button>
                {!isSingleton && (
                  <button onClick={cancel} className="text-[#4B5563] text-sm bsca-link inline-flex items-center gap-1">
                    <X className="h-3.5 w-3.5" /> Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
